import { useGetMyEventsQuery } from "queries/useGetMyEventsQuery";
import { atom, useAtomValue } from "jotai";
import { searchTermAtom } from "components/Event/Dashboard/utils/filtersAtom";
import { eventItemsGroupByGroupName } from "components/Event/Dashboard/utils/events.utils";
import { EventItem } from "common/event/event.types";
import { filter, orderBy, partition } from "lodash";
import { isContainSubString } from "@ic-anywhere/ic-utils";
import { isDateBeforeToday } from "src/utils/dateUtils";
import { INVITATION_STATUS } from "src/api/events/event.types";

export enum EVENT_STATUS {
  ALL,
  PENDING,
  ATTENDING,
  DECLINED,
  ISSUED,
}

export const activeFilterStatusAtom = atom<EVENT_STATUS>(EVENT_STATUS.ALL);

const hasMatchingStatus = (event: EventItem, statusFilter: EVENT_STATUS): boolean => {
  switch (statusFilter) {
    case EVENT_STATUS.PENDING:
      return INVITATION_STATUS.PENDING === event.invitationStatus;
    case EVENT_STATUS.ATTENDING:
      return event.invitationStatus === INVITATION_STATUS.ATTENDING;
    case EVENT_STATUS.DECLINED:
      return [INVITATION_STATUS.DECLINED_NOT_AVAILABLE, INVITATION_STATUS.DECLINED_NOT_INTERESTED].includes(
        event.invitationStatus
      );
    case EVENT_STATUS.ISSUED:
      return event.endDate && isDateBeforeToday(event.endDate);
    default:
      return true;
  }
};
const filterAndSortEvents = (events: EventItem[], searchTerm: string, statusFilter: EVENT_STATUS): EventItem[] => {
  const filteredEvents = filter(
    events,
    (event) => isContainSubString(event.name, searchTerm) && hasMatchingStatus(event, statusFilter)
  );
  const [passedEvents, eventsToCome] = partition(filteredEvents, (event) => isDateBeforeToday(event.endDate));
  const orderedEventsToCome = orderBy(eventsToCome, ["startDate"], ["desc"]);
  const orderedPassedEvents = orderBy(passedEvents, ["startDate"], ["desc"]);
  return [...orderedEventsToCome, ...orderedPassedEvents];
};

export const useEventsDashboard = (): { events: EventItem[]; isFetching: boolean } => {
  const { data, isFetching } = useGetMyEventsQuery();
  const status = useAtomValue(activeFilterStatusAtom);
  const searchTerm = useAtomValue(searchTermAtom);
  const mappedEvents = eventItemsGroupByGroupName(data);
  const filteredAndSortedEvents = filterAndSortEvents(mappedEvents, searchTerm, status);

  return {
    events: filteredAndSortedEvents,
    isFetching,
  };
};
